import { SortingDirection } from '@boltenergy-be/design-system/dist/components/data-table/types'
import { InvoiceCells, InvoiceTableDataRow } from 'pages/app/billing/invoices/table-data/types.ts'
import { store } from 'store/index.ts'
import { formatAddress } from 'utils/user.ts'
import { CustomerAccountingDocument, CustomerInvoice, CustomerPayment, DOCUMENT_TYPES } from '@boltenergy-be/shared'

/**
 * Type guard to check if a given document is a CustomerInvoice
 * @param {CustomerAccountingDocument} document
 * @returns {document is CustomerInvoice}
 */
export const isCustomerInvoice = (document: CustomerAccountingDocument): document is CustomerInvoice => {
  return document.documentType === DOCUMENT_TYPES.invoice
}

/**
 * Type guard to check if a given document is a CustomerPayment
 * @param {} document
 * @returns {document is }
 */
export const isCustomerPayment = (document: CustomerAccountingDocument): document is CustomerPayment => {
  return document.documentType === DOCUMENT_TYPES.payment
}

/**
 * Returns a paginated an array
 * @template T - Generic type for input & return value
 * @param {T[]} arr
 * @param {number} size
 */
export const paginate = <T>(arr: T[], size: number): T[][] => {
  return arr.reduce<T[][]>((acc, val, i) => {
    const idx = Math.floor(i / size)
    const page = acc[idx] || (acc[idx] = [])
    page.push(val)
    return acc
  }, [])
}

/**
 * Returns the rawData from a given table cell.
 *
 * @param {unknown} rawData
 */
const getCellData = (rawData: unknown) => (typeof rawData === 'string' ? rawData.toUpperCase() : (rawData ?? ''))

/**
 * Sorts a Rows[] by one given key, in a 'asc' or 'desc' order.
 * @param {string} key
 * @param {SortingDirection} order
 */
export const sortRowsByKey =
  (key: InvoiceCells, order: SortingDirection = 'asc') =>
  (rowA: InvoiceTableDataRow, rowB: InvoiceTableDataRow) => {
    let comparison = 0
    const cellsA = rowA.entries[key]
    const cellsB = rowB.entries[key]

    if (!cellsA || !cellsB) return comparison

    const rawDataA = getCellData(cellsA)
    const rawDataB = getCellData(cellsB)

    if (rawDataA > rawDataB) {
      comparison = 1
    } else if (rawDataA < rawDataB) {
      comparison = -1
    }

    return order === 'desc' ? comparison * -1 : comparison
  }

/**
 * Find the electricity contract address in the billing contracts dictionary based on the contract number
 * @param {CustomerAccountingDocument} document
 * @returns {string|null}
 */
export const getAddressByDocumentContractNumber = ({ document }: { document: CustomerAccountingDocument }): string | null => {
  if ('electricityContractNumber' in document) {
    const billingContracts = store.getState().contact.billingContracts
    const billingContract = Object.values(billingContracts).find(
      (contract) => contract.serviceContracts.electricity.contractNumber === document.electricityContractNumber
    )
    const electricityContract = billingContract?.serviceContracts.electricity

    if (electricityContract) {
      return formatAddress(electricityContract.deliveryPoint?.address)
    }
  }

  return null
}
